
import { useRef } from "react"
import gsap from "gsap"
import { useGSAP } from "@gsap/react"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import BaseHeadline from "../general/BaseHeadline"
import { BaseCardContainer, BaseCardContent } from "../general/BaseCard"
import BaseText from "../general/BaseText"

gsap.registerPlugin(useGSAP, ScrollTrigger)

export type ProcessSectionHeadingType = {
    label?: string
    headline: string
    text?: string
}

export type ProcessSectionStepType = {
    attributes: {
        number: string
        headline: string
        description: string
    }
}

type ProcessSectionType = {
    heading: ProcessSectionHeadingType
    steps: ProcessSectionStepType[]
    classProps?: string
}

export default function ProcessSection({ heading, steps, classProps }: ProcessSectionType) {
    const sectionRef = useRef<HTMLElement | null>(null)

    useGSAP(
        () => {
            const section = sectionRef.current
            if (!section) return

            const progressLine = section.querySelector("[data-process-progress-line]")
            const stepElements = gsap.utils.toArray<HTMLElement>("[data-process-step]", section)

            const stepStart = "top 72%"

            if (progressLine && stepElements.length > 0) {
                const firstStep = stepElements[0]
                const lastStep = stepElements[stepElements.length - 1]

                gsap.set(progressLine, { scaleY: 0, transformOrigin: "top top" })

                gsap.fromTo(
                    progressLine,
                    { scaleY: 0 },
                    {
                        scaleY: 1,
                        ease: "none",
                        scrollTrigger: {
                            trigger: firstStep,
                            endTrigger: lastStep,
                            start: stepStart,
                            end: stepStart,
                            scrub: true,
                        },
                    }
                )
            }

            stepElements.forEach((step) => {
                const card = step.querySelector<HTMLElement>("[data-process-card]")
                const numberContainer = step.querySelector<HTMLElement>("[data-process-number-container]")
                const isMobile = window.matchMedia("(max-width: 639px)").matches

                if (!card || !numberContainer) return

                gsap.set(
                    card,
                    isMobile
                        ? { y: 80, opacity: 0, scale: 0.9, transformOrigin: "center center" }
                        : { x: 120, opacity: 0, scale: 0.9, transformOrigin: "center center" }
                )
                gsap.set(numberContainer, { scale: 0, opacity: 0, transformOrigin: "center center" })

                gsap.timeline({
                    scrollTrigger: {
                        trigger: step,
                        start: stepStart,
                        toggleActions: "play none none reverse",
                    },
                })
                    .to(
                        card,
                        isMobile
                            ? {
                                y: 0,
                                scale: 1,
                                opacity: 1,
                                ease: "power2.out",
                                duration: 0.7,
                            }
                            : {
                                x: 0,
                                scale: 1,
                                opacity: 1,
                                ease: "power2.out",
                                duration: 0.7,
                            },
                        0
                    )
                    .to(
                        numberContainer,
                        {
                            scale: 1,
                            opacity: 1,
                            ease: "power2.out",
                            duration: 0.7,
                        },
                        0
                    )
            })

            ScrollTrigger.refresh()
        },
        { scope: sectionRef, dependencies: [steps.length, heading.text] }
    )

    return (
        <section ref={sectionRef} className={`relative overflow-hidden py-24 md:py-32 px-4 md:px-8 xl:px-0 ${classProps ?? ""}`}>
            <div className="max-w-page mx-auto">
                {heading && (
                    <div className="mb-16 md:mb-24 flex flex-col items-center text-center md:text-left md:items-end md:flex-row justify-between md:gap-8 relative">
                        <BaseHeadline
                            width="md:w-2/5"
                            label={heading.label}
                            headline={heading.headline}
                            headlineClassProps="leading-tight"
                            classProps="pb-0 items-center md:items-start"

                        />
                        {heading.text && (
                            <div className="md:w-1/3 mx-auto md:mx-0 text-center md:text-left">
                                <BaseText text={heading.text} classProps="leading-relaxed" padding="py-0" textSize="text-base md:text-lg" />
                            </div>
                        )}
                    </div>
                )}
                <div data-process-timeline className="relative max-w-4xl mx-auto">
                    <div className="absolute left-1/2 -translate-x-1/2 md:translate-x-0 md:left-[39px] top-4 bottom-4 w-[2px] bg-base-content/5" />

                    <div data-process-progress-line className="absolute left-1/2 -translate-x-1/2 md:translate-x-0 md:left-[39px] top-4 bottom-4 w-[2px] bg-gradient-to-b from-primary via-secondary to-primary"></div>

                    <div className="flex flex-col gap-12 relative z-10">
                        {steps?.map((step) => {
                            return (
                                <div data-process-step key={`${step.attributes.number}-${step.attributes.headline}`} className="relative flex flex-col items-center text-center md:text-left md:flex-row md:items-center w-full group">
                                    <div className="hidden md:flex w-20 h-20 items-center justify-center shrink-0">
                                        <div
                                            data-process-number-container
                                            className="w-16 h-16 bg-base-100 border-2 border-base-content/10 rounded-2xl flex items-center justify-center z-10 shadow-[0_0_0_8px_var(--fallback-b2,oklch(var(--b2)))]">
                                            <span data-process-number-text className="font-headline font-black text-xl text-primary">{step.attributes.number}</span>
                                        </div>
                                    </div>

                                    <div className="w-full md:pl-16 flex-1">
                                        <div data-process-card>
                                            <BaseCardContainer className="max-w-2xl mx-auto md:mx-0 bg-base-100 p-8 border-base-content/5">
                                                <BaseCardContent className="px-0 pb-0">
                                                    <span className="md:hidden font-headline font-black text-xl text-primary block mb-2">{step.attributes.number}</span>
                                                    <h3 data-process-title className="font-headline text-2xl font-bold text-base-content mb-3">{step.attributes.headline}</h3>
                                                    <BaseText text={step.attributes.description} classProps="leading-relaxed" textSize="text-base md:text-lg" padding="py-0" />
                                                </BaseCardContent>
                                            </BaseCardContainer>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </section>
    )
}
